import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Phone, Mail, Calendar, Paperclip, MessageSquare, Send, X, FileText, Image as ImageIcon, UserCheck, Plus, GraduationCap, LayoutGrid, List } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import KanbanBoard from "../components/KanbanBoard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StudentProfileView from "./StudentProfileView";
import { ApprovalHistory } from "../components/ApprovalHistory";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", Applied: "bg-cyan-100 text-cyan-700",
  Admitted: "bg-green-100 text-green-700", Deposit: "bg-teal-100 text-teal-700",
  Visa: "bg-indigo-100 text-indigo-700", Closed: "bg-slate-100 text-slate-600",
  Lost: "bg-red-100 text-red-700",
};
const BAND_COLORS = { Hot: "bg-red-100 text-red-700", Warm: "bg-amber-100 text-amber-700", Cold: "bg-slate-100 text-slate-500" };
const STATUS_TINT = {
  New: "#2563eb", Contacted: "#d97706", Qualified: "#7c3aed", Applied: "#0891b2",
  Admitted: "#16a34a", Deposit: "#0d9488", Visa: "#4f46e5", Closed: "#64748b", Lost: "#dc2626",
};

export default function Leads() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [viewProfileEmail, setViewProfileEmail] = useState(null);
  const [eduLevels, setEduLevels] = useState([]);
  const [feeBands, setFeeBands] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [countries, setCountries] = useState([]);
  const [dial, setDial] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") || "All");
  const [view, setView] = useState("table");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [countryFilter, setCountryFilter] = useState("All");
  const moveLead = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.put(`/leads/${id}`, { status }); toast({ title: `Moved to ${status}` }); }
    catch { toast({ title: "Could not update", variant: "destructive" }); loadList(); }
  };
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [apprKey, setApprKey] = useState(0);
  const [comment, setComment] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [nl, setNl] = useState({ name: "", email: "", dial_code: "+60", phone: "", course: "", country: "", budget: "", education_level: "", fee_capacity: "", message: "", assigned_to: "" });
  const [saving, setSaving] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [cv, setCv] = useState({ university_id: "", programme: "", intake: "", level: "", campus: "", field_of_study: "", nationality: "", dob: "", prev_education: "" });
  const [converting, setConverting] = useState(false);
  const fileRef = useRef(null);

  const selectedUni = universities.find((u) => u.id === cv.university_id);

  const loadList = () => {
    api.get("/leads", { params: filter !== "All" ? { status: filter } : {} }).then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(() => { loadList(); }, [filter]);
  useEffect(() => {
    api.get("/leads/meta").then((r) => { setStatuses(r.data.statuses); setEduLevels(r.data.education_levels || []); setFeeBands(r.data.fee_capacity_bands || []); }).catch(() => {});
    api.get("/leads/assignees").then((r) => setAssignees(r.data)).catch(() => {});
    api.get("/masters/countries").then((r) => setCountries(r.data)).catch(() => {});
    api.get("/auth/dial-codes").then((r) => setDial(r.data)).catch(() => {});
    api.get("/universities").then((r) => setUniversities(r.data)).catch(() => {});
  }, []);

  const openConvert = () => {
    setCv({ university_id: "", programme: detail?.course || "", intake: "", level: "", campus: "", field_of_study: "", nationality: "", dob: "", prev_education: "" });
    setConvertOpen(true);
  };

  const convertToApplication = async () => {
    if (!cv.university_id) { toast({ title: "Please select a university", variant: "destructive" }); return; }
    setConverting(true);
    try {
      const { data } = await api.post("/applications/from-lead", { lead_id: active, ...cv });
      if (data?.pending_approval) {
        toast({ title: "Submitted for approval", description: data.message || "Lead conversion awaits approval per the matrix." });
      } else {
        toast({
          title: "Application created",
          description: data?.notify_email_sent
            ? "Lead marked Applied. Confirmation email sent to the student."
            : "Lead marked Applied. (Student email will send once Resend keys are added.)",
        });
      }
      setConvertOpen(false);
      refreshDetail();
    } catch (e) {
      toast({ title: "Conversion failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setConverting(false);
    }
  };

  const createLead = async () => {
    if (!nl.name || !nl.email || !nl.phone) { toast({ title: "Name, email & phone are required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads", { ...nl, source: `Manual · ${user?.name || "Staff"}` });
      // optional assign on create
      if (nl.assigned_to) {
        const a = assignees.find((x) => x.id === nl.assigned_to);
        await api.put(`/leads/${data.id}`, { assigned_to: nl.assigned_to, assigned_to_name: a ? a.name : "" });
      }
      toast({
        title: data?.is_duplicate ? "Lead added — possible duplicate" : "Lead added",
        description: `Score ${data?.score ?? "—"} (${data?.score_band || "—"})${data?.is_duplicate ? " · matches an existing lead" : ""}`,
        variant: data?.is_duplicate ? "destructive" : undefined,
      });
      setNewOpen(false);
      setNl({ name: "", email: "", dial_code: "+60", phone: "", course: "", country: "", budget: "", education_level: "", fee_capacity: "", message: "", assigned_to: "" });
      loadList();
    } catch (e) { toast({ title: "Failed to add lead", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const openLead = async (id) => {
    setActive(id);
    const { data } = await api.get(`/leads/${id}`);
    setDetail(data);
  };
  const refreshDetail = async () => { if (active) { const { data } = await api.get(`/leads/${active}`); setDetail(data); setApprKey((k) => k + 1); loadList(); } };

  const setField = async (field, value) => {
    const payload = { [field]: value };
    if (field === "assigned_to") {
      const a = assignees.find((x) => x.id === value);
      payload.assigned_to_name = a ? a.name : "";
    }
    await api.put(`/leads/${active}`, payload);
    refreshDetail();
    toast({ title: "Lead updated" });
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/leads/${active}/comments`, { text: comment });
    setComment(""); refreshDetail();
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const kind = file.type.startsWith("image") ? "image" : "file";
      await api.post(`/leads/${active}/files`, { name: file.name, data: reader.result, kind });
      refreshDetail(); toast({ title: "File attached" });
    };
    reader.readAsDataURL(file);
  };

  const filtered = leads.filter((l) => JSON.stringify(l).toLowerCase().includes(q.toLowerCase()))
    .filter((l) => assigneeFilter === "All"
      || (assigneeFilter === "__unassigned" ? !l.assigned_to : l.assigned_to === assigneeFilter))
    .filter((l) => countryFilter === "All" || (l.country || "") === countryFilter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Leads CRM</h1>
          <p className="text-slate-500 mt-1 text-sm">Track, assign and nurture every enquiry to admission.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={18} className="mr-1" /> New Lead</Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mt-5">
        <div className="relative flex-1 max-w-sm">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="pl-10 h-11 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {["All", ...statuses].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3.5 py-1.5 rounded-full text-sm font-medium ${filter === s ? "bg-[#004030] text-white" : "bg-[#f0f6f3] text-[#004030]"}`}>{s}</button>
          ))}
          <div className="flex rounded-full border border-slate-200 overflow-hidden ml-1" data-testid="leads-view-toggle">
            <button onClick={() => setView("table")} title="Table view" className={`px-3 py-1.5 ${view === "table" ? "bg-[#004030] text-white" : "bg-white text-[#004030]"}`} data-testid="leads-view-table"><List size={15} /></button>
            <button onClick={() => setView("kanban")} title="Kanban view" className={`px-3 py-1.5 ${view === "kanban" ? "bg-[#004030] text-white" : "bg-white text-[#004030]"}`} data-testid="leads-view-kanban"><LayoutGrid size={15} /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="leads-kanban-filters">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Filter</span>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="h-9 w-48 rounded-full bg-white text-sm" data-testid="leads-filter-counsellor"><SelectValue placeholder="Counsellor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All counsellors</SelectItem>
            <SelectItem value="__unassigned">Unassigned</SelectItem>
            {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.kind})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="h-9 w-44 rounded-full bg-white text-sm" data-testid="leads-filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All countries</SelectItem>
            {countries.map((c) => <SelectItem key={c.id} value={c.name}>{c.flag} {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(assigneeFilter !== "All" || countryFilter !== "All") && (
          <button onClick={() => { setAssigneeFilter("All"); setCountryFilter("All"); }} data-testid="leads-filter-clear" className="text-xs font-semibold text-[#004030] hover:underline px-2">Clear</button>
        )}
      </div>

      {view === "kanban" ? (
        <div className="mt-5">
          <KanbanBoard
            columns={statuses}
            items={filtered}
            getId={(l) => l.id}
            getStatus={(l) => l.status}
            columnTint={STATUS_TINT}
            onMove={moveLead}
            onCardClick={(l) => openLead(l.id)}
            renderCard={(l) => (
              <div>
                <p className="font-semibold text-sm text-[#1a231f] flex items-center gap-1.5">{l.name}
                  {l.sla_breached && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-red-100 text-red-700">SLA</span>}
                </p>
                <p className="text-xs text-slate-500 truncate">{l.course || "—"}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${BAND_COLORS[l.score_band] || "bg-slate-100 text-slate-500"}`}>{l.score ?? 0} · {l.score_band || "—"}</span>
                  <span className="text-[10px] text-slate-400">{l.assigned_to_name || "Unassigned"}</span>
                </div>
              </div>
            )}
          />
        </div>
      ) : (
      <div className="bg-white rounded-2xl border border-slate-100 mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
            <th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Course</th>
            <th className="px-4 py-3 font-semibold">Country</th><th className="px-4 py-3 font-semibold">Score</th>
            <th className="px-4 py-3 font-semibold">Assigned</th>
            <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Activity</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((l) => (
              <tr key={l.id} onClick={() => openLead(l.id)} className="hover:bg-[#f7faf8] cursor-pointer" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#012a1e] flex items-center gap-1.5">{l.name}
                    {l.is_duplicate && <span data-testid="dup-badge" className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">DUP</span>}
                    {l.sla_breached && <span data-testid="sla-badge" className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">SLA</span>}
                  </p>
                  <p className="text-xs text-slate-400">{l.email}</p>
                </td>
                <td className="px-4 py-3 text-slate-600">{l.course || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{l.country || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${BAND_COLORS[l.score_band] || "bg-slate-100 text-slate-500"}`}>{l.score ?? 0} · {l.score_band || "—"}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">{l.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[l.status]}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-slate-500 text-xs"><span className="inline-flex items-center gap-1 mr-2"><MessageSquare size={13} />{l.comments_count}</span><span className="inline-flex items-center gap-1"><Paperclip size={13} />{l.files_count}</span></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      <Sheet open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display text-xl">{detail.name}</SheetTitle></SheetHeader>
              <div className="space-y-2 mt-3 text-sm">
                <p className="flex items-center gap-2 text-slate-600"><Mail size={15} className="text-[#004030]" /> {detail.email}</p>
                <p className="flex items-center gap-2 text-slate-600"><Phone size={15} className="text-[#004030]" /> {detail.dial_code} {detail.phone}</p>
                <p className="text-slate-500 text-xs">Source: {detail.generated_by} · {new Date(detail.created_at).toLocaleString()}</p>
                <button onClick={() => setViewProfileEmail(detail.email)} data-testid="view-lead-student-profile" className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-[#004030] hover:underline">
                  <GraduationCap size={14} /> View student profile
                </button>
              </div>

              {/* Scoring & SLA panel */}
              <div className="mt-4 rounded-xl border border-slate-100 bg-[#f7faf8] p-3 space-y-2" data-testid="lead-score-panel">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${BAND_COLORS[detail.score_band] || "bg-slate-100 text-slate-500"}`}>Score {detail.score ?? 0} · {detail.score_band || "—"}</span>
                  {detail.first_response_at
                    ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Responded ✓</span>
                    : detail.sla_breached
                      ? <>
                          <span data-testid="detail-sla-breached" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700">SLA breached</span>
                          <button data-testid="resolve-sla-btn" onClick={async () => {
                            try { await api.post(`/leads/${active}/resolve-sla`); toast({ title: "SLA breach resolved" }); refreshDetail(); }
                            catch { toast({ title: "Could not resolve", variant: "destructive" }); }
                          }} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#004030] text-white hover:bg-[#013d2b] transition-colors">Resolve SLA</button>
                        </>
                      : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Awaiting first response</span>}
                  {detail.is_duplicate && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-100 text-orange-700">Possible duplicate</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-500">Education level</label>
                    <Select value={detail.education_level || ""} onValueChange={(v) => setField("education_level", v)}>
                      <SelectTrigger className="h-9 mt-1 bg-white" data-testid="detail-education-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{eduLevels.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-xs text-slate-500">Fee capacity</label>
                    <Select value={detail.fee_capacity || ""} onValueChange={(v) => setField("fee_capacity", v)}>
                      <SelectTrigger className="h-9 mt-1 bg-white" data-testid="detail-fee-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{feeBands.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div><label className="text-xs text-slate-500">Status</label>
                  <Select value={detail.status} onValueChange={(v) => setField("status", v)}>
                    <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-slate-500">Assign to</label>
                  <Select value={detail.assigned_to || ""} onValueChange={(v) => setField("assigned_to", v)}>
                    <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.kind})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-xs text-slate-500 flex items-center gap-1"><Calendar size={13} /> Follow-up date &amp; time</label>
                <Input type="datetime-local" value={detail.followup_at ? detail.followup_at.slice(0, 16) : ""} onChange={(e) => setField("followup_at", e.target.value)} className="h-10 mt-1" />
              </div>

              {user?.kind !== "agent" && (
                <Button
                  onClick={openConvert}
                  data-testid="convert-to-application-button"
                  disabled={detail.status === "Applied"}
                  className="w-full mt-4 bg-[#c9a227] hover:bg-[#b8921f] text-[#012a1e] font-semibold h-11 rounded-full disabled:opacity-60"
                >
                  <GraduationCap size={17} className="mr-1.5" />
                  {detail.status === "Applied" ? "Already Applied" : "Convert to Application"}
                </Button>
              )}

              {/* Files */}
              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#012a1e] text-sm">Attachments</h3>
                  <button onClick={() => fileRef.current?.click()} className="text-xs text-[#004030] font-semibold flex items-center gap-1"><Paperclip size={13} /> Add file</button>
                  <input ref={fileRef} type="file" hidden onChange={onFile} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {(detail.files || []).map((f) => (
                    <a key={f.id} href={f.data} target="_blank" rel="noopener noreferrer" className="border border-slate-200 rounded-lg p-2 text-center hover:border-[#004030]">
                      {f.kind === "image" ? <img src={f.data} alt={f.name} className="h-14 w-full object-cover rounded" /> : <FileText size={28} className="mx-auto text-[#004030]" />}
                      <p className="text-[10px] truncate mt-1">{f.name}</p>
                    </a>
                  ))}
                  {!(detail.files || []).length && <p className="text-xs text-slate-400 col-span-3">No attachments yet.</p>}
                </div>
              </div>

              {/* Comments */}
              <ApprovalHistory entityType="lead" entityId={active} refreshKey={apprKey} />

              <div className="mt-5">
                <h3 className="font-semibold text-[#012a1e] text-sm">Comments &amp; Notes</h3>
                <div className="space-y-2 mt-2 max-h-52 overflow-y-auto">
                  {(detail.comments || []).map((c) => (
                    <div key={c.id} className="bg-[#f7faf8] rounded-lg p-3">
                      <p className="text-sm text-slate-700">{c.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{c.by_name} · {new Date(c.at).toLocaleString()}</p>
                    </div>
                  ))}
                  {!(detail.comments || []).length && <p className="text-xs text-slate-400">No comments yet.</p>}
                </div>
                <div className="flex gap-2 mt-3">
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment..." className="h-10" />
                  <Button onClick={addComment} className="bg-[#004030] text-white h-10 px-3"><Send size={16} /></Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New Lead dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add New Lead</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Full Name *</Label><Input value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} className="h-11" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={nl.email} onChange={(e) => setNl({ ...nl, email: e.target.value })} className="h-11" /></div>
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <div className="flex gap-2">
                <Select value={nl.dial_code} onValueChange={(v) => setNl({ ...nl, dial_code: v })}>
                  <SelectTrigger className="h-11 w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>{dial.map((d) => <SelectItem key={d.code} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={nl.phone} onChange={(e) => setNl({ ...nl, phone: e.target.value })} className="h-11 flex-1" placeholder="Phone number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Course</Label><Input value={nl.course} onChange={(e) => setNl({ ...nl, course: e.target.value })} className="h-11" placeholder="e.g. MBBS" /></div>
              <div className="space-y-2"><Label>Country</Label>
                <Select value={nl.country} onValueChange={(v) => setNl({ ...nl, country: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.name}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Budget / year</Label><Input value={nl.budget} onChange={(e) => setNl({ ...nl, budget: e.target.value })} className="h-11" placeholder="e.g. $10k-$25k" /></div>
              <div className="space-y-2"><Label>Assign to</Label>
                <Select value={nl.assigned_to} onValueChange={(v) => setNl({ ...nl, assigned_to: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Auto-assign" /></SelectTrigger>
                  <SelectContent>{assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.kind})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Education level</Label>
                <Select value={nl.education_level} onValueChange={(v) => setNl({ ...nl, education_level: v })}>
                  <SelectTrigger className="h-11" data-testid="new-education-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{eduLevels.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Fee capacity</Label>
                <Select value={nl.fee_capacity} onValueChange={(v) => setNl({ ...nl, fee_capacity: v })}>
                  <SelectTrigger className="h-11" data-testid="new-fee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{feeBands.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={nl.message} onChange={(e) => setNl({ ...nl, message: e.target.value })} className="min-h-20" placeholder="Anything about this lead..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createLead} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white">Add Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Application dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Convert Lead to Application</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Creating an application for <span className="font-semibold text-[#012a1e]">{detail?.name}</span>. This will mark the lead as <span className="font-semibold">Applied</span>.</p>
            <div className="space-y-2">
              <Label>University *</Label>
              <Select value={cv.university_id} onValueChange={(v) => setCv({ ...cv, university_id: v, programme: cv.programme, level: "" })}>
                <SelectTrigger className="h-11" data-testid="convert-university-select"><SelectValue placeholder="Select a university" /></SelectTrigger>
                <SelectContent>{universities.map((u) => <SelectItem key={u.id} value={u.id}>{u.flag} {u.name} — {u.country}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Programme</Label>
                {selectedUni?.programmes?.length ? (
                  <Select value={cv.programme} onValueChange={(v) => setCv({ ...cv, programme: v })}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{selectedUni.programmes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={cv.programme} onChange={(e) => setCv({ ...cv, programme: e.target.value })} className="h-11" placeholder="e.g. MBBS" />
                )}
              </div>
              <div className="space-y-2">
                <Label>Level</Label>
                {selectedUni?.levels?.length ? (
                  <Select value={cv.level} onValueChange={(v) => setCv({ ...cv, level: v })}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{selectedUni.levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={cv.level} onChange={(e) => setCv({ ...cv, level: e.target.value })} className="h-11" placeholder="e.g. Undergraduate" />
                )}
              </div>
            </div>
            <div className="space-y-2"><Label>Intake</Label><Input value={cv.intake} onChange={(e) => setCv({ ...cv, intake: e.target.value })} className="h-11" placeholder="e.g. Sep 2026" /></div>
            {selectedUni?.campuses?.length ? (
              <div className="space-y-2"><Label>Campus</Label>
                <Select value={cv.campus} onValueChange={(v) => setCv({ ...cv, campus: v })}>
                  <SelectTrigger className="h-11" data-testid="convert-campus"><SelectValue placeholder="Select campus" /></SelectTrigger>
                  <SelectContent>{selectedUni.campuses.map((c, i) => <SelectItem key={i} value={c.name || c}>{c.name || c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Field of study</Label><Input value={cv.field_of_study} onChange={(e) => setCv({ ...cv, field_of_study: e.target.value })} className="h-11" placeholder="e.g. Computer Science" data-testid="convert-field" /></div>
              <div className="space-y-2"><Label>Nationality</Label><Input value={cv.nationality} onChange={(e) => setCv({ ...cv, nationality: e.target.value })} className="h-11" placeholder="e.g. Malaysian" data-testid="convert-nationality" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date of birth</Label><Input type="date" value={cv.dob} onChange={(e) => setCv({ ...cv, dob: e.target.value })} className="h-11" data-testid="convert-dob" /></div>
              <div className="space-y-2"><Label>Previous education</Label><Input value={cv.prev_education} onChange={(e) => setCv({ ...cv, prev_education: e.target.value })} className="h-11" placeholder="e.g. A-Levels, ABC School" data-testid="convert-prev-edu" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button onClick={convertToApplication} disabled={converting} data-testid="convert-submit-button" className="bg-[#004030] hover:bg-[#013d2b] text-white">Create Application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StudentProfileView open={!!viewProfileEmail} onClose={() => setViewProfileEmail(null)} email={viewProfileEmail} />
    </div>
  );
}
